import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useCustomer } from "../context/CustomerContext";

const GoogleIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/></svg>
);

export default function AccountLogin() {
  const { customer, setCustomer } = useCustomer();
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin"); // signin | register | forgot
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  if (customer) return <Navigate to="/account" replace />;

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "forgot") {
        const { data } = await api.post("/account/forgot-password", { email: form.email, origin_url: window.location.origin });
        toast.success(data.message);
        setMode("signin");
      } else {
        const url = mode === "register" ? "/account/register" : "/account/login";
        const { data } = await api.post(url, mode === "register" ? form : { email: form.email, password: form.password });
        setCustomer(data);
        toast.success(`Kia ora, ${data.name || data.email}!`);
        navigate("/account");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pt-32 pb-24 px-5 bg-cream min-h-screen" data-testid="account-login-page">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <p className="font-serif-display uppercase text-magenta text-[11px] tracking-[0.4em]">THE BLOOM FOUNDRY</p>
          <h1 className="font-serif-display uppercase text-plum-deep text-4xl sm:text-5xl mt-2">
            {mode === "register" ? "JOIN THE FOUNDRY" : mode === "forgot" ? "RESET PASSWORD" : "FOUNDRY PROFILE"}
          </h1>
          <p className="text-sm text-plum-deep/60 mt-3">Your orders, Bloom passports and vouchers — all in one sunny spot.</p>
        </div>

        <div className="bg-warmwhite border-[3px] border-plum-deep rounded-[2rem] p-6 md:p-8 space-y-4">
          {mode !== "forgot" && (
            <>
              <button onClick={googleSignIn} data-testid="google-signin-btn"
                className="w-full flex items-center justify-center gap-3 rounded-full bg-cream border-2 border-plum-deep/20 py-3.5 text-sm font-semibold text-plum-deep hover:border-magenta transition-colors">
                <GoogleIcon /> CONTINUE WITH GOOGLE
              </button>
              <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] text-plum-deep/40 font-bold">
                <span className="flex-1 h-px bg-plum-deep/15" /> OR WITH EMAIL <span className="flex-1 h-px bg-plum-deep/15" />
              </div>
            </>
          )}
          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name *" data-testid="account-name-input"
                className="w-full rounded-full px-5 py-3 bg-cream border-2 border-plum-deep/15 text-sm focus:outline-none focus:border-magenta" />
            )}
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email *" data-testid="account-email-input"
              className="w-full rounded-full px-5 py-3 bg-cream border-2 border-plum-deep/15 text-sm focus:outline-none focus:border-magenta" />
            {mode !== "forgot" && (
              <input required type="password" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={mode === "register" ? "Password (8+ characters) *" : "Password *"} data-testid="account-password-input"
                className="w-full rounded-full px-5 py-3 bg-cream border-2 border-plum-deep/15 text-sm focus:outline-none focus:border-magenta" />
            )}
            <button type="submit" disabled={busy} data-testid="account-submit-btn"
              className="w-full rounded-full bg-magenta text-cream py-3.5 font-serif-display uppercase text-sm tracking-[0.16em] font-bold hover:bg-plum transition-colors disabled:opacity-50">
              {busy ? "ONE MOMENT…" : mode === "register" ? "CREATE MY PROFILE" : mode === "forgot" ? "SEND RESET LINK" : "SIGN IN"}
            </button>
          </form>
          <div className="text-center text-xs text-plum-deep/60 space-y-1.5 pt-1">
            {mode === "signin" && (
              <>
                <p><button onClick={() => setMode("forgot")} data-testid="forgot-password-link" className="underline hover:text-magenta">Forgotten your password?</button></p>
                <p>New here? <button onClick={() => setMode("register")} data-testid="switch-register" className="underline font-semibold hover:text-magenta">Create a Foundry Profile</button></p>
              </>
            )}
            {mode !== "signin" && (
              <p><button onClick={() => setMode("signin")} data-testid="switch-signin" className="underline hover:text-magenta">← Back to sign in</button></p>
            )}
          </div>
        </div>
        <p className="text-center text-xs text-plum-deep/50 mt-6">
          Just want to shop? No profile needed — <Link to="/supply-room" className="underline">browse the Supply Room</Link>.
        </p>
      </div>
    </div>
  );
}
